import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import ShopLayout from '../../components/layout/ShopLayout';
import './ProductDetail.css';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function ProductDetail() {
  const { id } = useParams();
  const { products, categories, shippingZones, loading, orders } = useData();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [wilaya, setWilaya] = useState('');
  const [city, setCity] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null);
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [shippingInfo, setShippingInfo] = useState({ price: 0, isFree: false });
  const [deliveryType, setDeliveryType] = useState(null);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);

  const product = products.find(p => p.id === id);
  const category = product ? categories.find(c => c.id === product.categoryId) : null;

  useEffect(() => {    
    if (product) {        
      const categoryProducts = products
        .filter(p => p.categoryId === product.categoryId && p.id !== product.id && p.isActive)
        .slice(0, 4);
      setSuggestedProducts(categoryProducts);
    }
  }, [product, products]);

  useEffect(() => {
    if (phone && wilaya && product) {
      if (product.freeShipping) {
        setShippingInfo({ 
          price: 0, 
          isFree: true, 
          reason: 'Produit éligible à la livraison gratuite' 
        });
      } else {
        const pendingOrders = orders.filter(order => 
          order.customer.phone === phone && 
          ['pending', 'confirmed'].includes(order.status)
        );

        const hasPendingOrderInSameWilaya = pendingOrders.some(order => {
          const orderWilaya = order.customer.wilaya;
          const selectedWilaya = wilaya;
          
          console.log('Comparaison wilaya:', {
            commandeWilaya: orderWilaya,
            wilayaSelectionnee: selectedWilaya,
            sontIdentiques: orderWilaya === selectedWilaya
          });
          
          return orderWilaya === selectedWilaya;
        });

        if (hasPendingOrderInSameWilaya) {
          setShippingInfo({
            price: 0,
            isFree: true,
            reason: 'Commande en attente détectée dans cette wilaya'
          });
        } else {
          const shippingZone = shippingZones.find(zone => zone.wilaya === wilaya);
          setShippingInfo({
            price: shippingZone?.price || 0,
            isFree: false,
            reason: ''
          });
        }
      }
    }
  }, [phone, wilaya, orders, product, shippingZones]);

  useEffect(() => {
    if (products.length > 0) {
      const otherProducts = products.filter(p => 
        p.id !== id && p.isActive && p.stock > 0
      );
      
      const shuffled = [...otherProducts].sort(() => 0.5 - Math.random());
      setRecommendedProducts(shuffled.slice(0, 4));
    }
  }, [products, id]);

  useEffect(() => {
    if (product && product.hasVariants && product.variants && product.variants.length > 0) {
      setSelectedVariant(product.variants[0]);
    } else {
      setSelectedVariant(null);
    }
  }, [product]);

  const isValidAlgerianPhone = (phone) => {
    const phoneRegex = /^(?:\+213|0)[5-7]\d{8}$/;
    return phoneRegex.test(phone);
  };

  if (loading) {
    return (
      <ShopLayout>
        <div className="loading-container">
          <div className="loading-content">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Chargement du produit...</p>
          </div>
        </div>
      </ShopLayout>
    );
  }

  if (!product) {
    return (
      <ShopLayout>
        <div className="error-container">
          <h2>Produit non trouvé</h2>
          <p>Le produit que vous recherchez n'existe pas.</p>
        </div>
      </ShopLayout>
    );
  }

  const handleFirstNameChange = (e) => {
    console.log('Changement prénom:', e.target.value);
    setFirstName(e.target.value);
  };

  const handleLastNameChange = (e) => {
    console.log('Changement nom:', e.target.value);
    setLastName(e.target.value);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    console.log('Changement téléphone:', value);
    setPhone(value);
    
    if (value && !isValidAlgerianPhone(value)) {
      e.target.setCustomValidity('Format: 0555123456 ou +213555123456');
    } else {
      e.target.setCustomValidity('');
    }
  };

  const handleWilayaChange = (e) => {
    const newWilaya = e.target.value;
    console.log('Changement wilaya:', newWilaya);
    setWilaya(newWilaya);
    setDeliveryType(null);
  };

  const handleCityChange = (e) => {
    console.log('Changement ville:', e.target.value);
    setCity(e.target.value);
  };

  const getCurrentPrice = () => {
    if (product && product.hasVariants && selectedVariant) {
      return selectedVariant.promoPrice && product.hasPromotion ? selectedVariant.promoPrice : selectedVariant.price;
    }
    return product && product.hasPromotion && product.promoPrice ? product.promoPrice : (product ? product.price : 0);
  };

  const getOriginalPrice = () => {
    if (product && product.hasVariants && selectedVariant) {
      return product.hasPromotion && selectedVariant.promoPrice && selectedVariant.promoPrice > 0 ? selectedVariant.price : null;
    }
    return product && product.hasPromotion && product.promoPrice && product.promoPrice > 0 ? product.price : null;
  };

  const calculateTotal = () => {
    if (!product) return 0;
    const subtotal = getCurrentPrice() * quantity;
    
    if (product.freeShipping || shippingInfo.isFree) {
      return subtotal;
    }

    if (deliveryType && shippingZones.find(zone => zone.wilaya === wilaya)) {
      const shippingCost = deliveryType === 'home' 
        ? shippingZones.find(zone => zone.wilaya === wilaya).homeDeliveryPrice 
        : shippingZones.find(zone => zone.wilaya === wilaya).officePickupPrice;
      return subtotal + shippingCost;
    }

    return subtotal;
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    if (!firstName.trim()) {
      toast.error('Veuillez saisir votre prénom');
      return;
    }
    if (!lastName.trim()) {
      toast.error('Veuillez saisir votre nom');
      return;
    }
    if (!phone.trim()) {
      toast.error('Veuillez saisir votre numéro de téléphone');
      return;
    }
    if (!isValidAlgerianPhone(phone)) {
      toast.error('Veuillez saisir un numéro de téléphone algérien valide');
      return;
    }
    if (!wilaya) {
      toast.error('Veuillez sélectionner votre wilaya');
      return;
    }
    if (!city.trim()) {
      toast.error('Veuillez saisir votre ville');
      return;
    }
    if (!deliveryType) {
      toast.error('Veuillez sélectionner un type de livraison');
      return;
    }

    setSubmitting(true);
    setOrderStatus(null);

    try {
      const shippingZone = shippingZones.find(zone => zone.wilaya === wilaya);
      const shippingPrice = deliveryType === 'home' 
        ? shippingZone.homeDeliveryPrice 
        : shippingZone.officePickupPrice;

      const orderData = {
        customer: {
          firstName,
          lastName,
          phone,
          wilaya,
          city
        },
        product: {
          id: product.id,
          name: product.name,
          price: getCurrentPrice(),
          quantity,
          subtotal: getCurrentPrice() * quantity,
          variant: selectedVariant ? {
            id: selectedVariant.id,
            name: selectedVariant.name,
            price: selectedVariant.price
          } : null
        },
        shipping: {
          type: deliveryType,
          price: shippingInfo.isFree ? 0 : shippingPrice,
          isFree: shippingInfo.isFree,
          freeReason: shippingInfo.reason || ''
        },
        totalPrice: calculateTotal(),
        status: 'pending',
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'orders'), orderData);

      setOrderStatus({
        type: 'success',
        message: 'Votre commande a été enregistrée avec succès! Nous vous contacterons bientôt.'
      });

      // Reset form
      setFirstName('');
      setLastName('');
      setPhone('');
      setWilaya('');
      setCity('');
      setQuantity(1);
      setDeliveryType(null);
      
    } catch (error) {
      console.error('Erreur lors de la commande:', error);
      setOrderStatus({
        type: 'error',
        message: 'Une erreur est survenue lors de l\'enregistrement de votre commande.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedShippingZone = shippingZones.find(zone => 
    zone.wilaya === wilaya
  );

  return (
    <ShopLayout>
      <div className="product-detail-container">
        <div className="product-hero">
          <div className="product-detail-content">
            <div className="product-gallery">
              <div className="main-image">
                {product.images?.[selectedImage] ? (
                  <img 
                    src={product.images[selectedImage].url} 
                    alt={product.name}
                  />
                ) : (
                  <div className="no-image">
                    <i className="fas fa-image"></i>
                  </div>
                )}
               
              </div>
              {product.images?.length > 1 && (
                <div className="thumbnail-grid">
                  {product.images.map((image, index) => (
                    <div 
                      key={index}
                      className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                      onClick={() => setSelectedImage(index)}
                    >
                      <img src={image.url} alt={`${product.name} - vue ${index + 1}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="product-info">
              <nav className="product-breadcrumb">
                <span>Shop</span>
                <i className="fas fa-chevron-right"></i>
                <span>{category?.name}</span>
              </nav>

              <div className="product-header">
                <h1>{product.name}</h1>
                <div className="product-meta">
                  <div className="price-badge">
                    {getOriginalPrice() && (
                      <span className="original-price">{getOriginalPrice()} DZD</span>
                    )}
                    <span className={product.hasPromotion ? 'promo-price' : ''}>
                      {getCurrentPrice()} DZD
                    </span>
                    {product.hasVariants && selectedVariant && (
                      <span className="variant-label"> ({selectedVariant.name})</span>
                    )}
                  </div>
                  {product.hasPromotion && (
                    <div className="promo-badge">
                      <i className="fas fa-tags"></i> Promotion
                    </div>
                  )}
                  <div className={`stock-badge ${product.stock > 0 ? 'in' : 'out'}`}>
                    <i className="fas fa-box"></i>
                    {product.stock > 0 ? `${product.stock} en stock` : 'Rupture de stock'}
                  </div>
                  {product.isBio && (
                    <div className="bio-badge">
                      <i className="fas fa-leaf"></i> BIO
                    </div>
                  )}
                </div>
              </div>

              {product.hasVariants && product.variants && product.variants.length > 0 && (
                <div className="product-variants">
                  <h3>Options de {product.variantType === 'color' ? 'couleur' : 
                                product.variantType === 'size' ? 'taille' : 
                                product.variantType === 'material' ? 'matériau' : 
                                product.variantType === 'style' ? 'style' : 'produit'}</h3>
                  <div className="variants-grid">
                    {product.variants.map(variant => (
                      <button
                        key={variant.id}
                        className={`variant-button ${selectedVariant?.id === variant.id ? 'selected' : ''}`}
                        onClick={() => setSelectedVariant(variant)}
                      >
                        <span className="variant-name">{variant.name}</span>
                        <div className="variant-price-container">
                          {product.hasPromotion && variant.promoPrice && variant.promoPrice > 0 ? (
                            <>
                              <span className="variant-original-price">{variant.price} DZD</span>
                              <span className="variant-promo-price">{variant.promoPrice} DZD</span>
                            </>
                          ) : (
                            <span className="variant-price">{variant.price} DZD</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="product-description">
                <h3>Description du produit</h3>
              </div>
              {product.description}

              {product.freeShipping && (
                <div className="shipping-info">
                  <i className="fas fa-truck"></i>
                  <div>
                    <h4>Livraison gratuite</h4>
                    <p>Pour cette commande</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="order-section-container">
          <div className="order-section-content">
            <h2>Commander ce produit</h2>
            
            {orderStatus && (
              <div className={`alert ${orderStatus.type}`}>
                {orderStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmitOrder} className="order-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">Prénom</label>
                  <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={handleFirstNameChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Nom</label>
                  <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={handleLastNameChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phone">
                  Numéro de téléphone
                  <span className="input-hint">(Format: 0555123456 ou +213555123456)</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={handlePhoneChange}
                  pattern="^(?:\+213|0)[5-7]\d{8}$"
                  required
                  className={phone && !isValidAlgerianPhone(phone) ? 'invalid' : ''}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="wilaya">Wilaya</label>
                  <select
                    id="wilaya"
                    value={wilaya}
                    onChange={handleWilayaChange}
                    required
                  >
                    <option value="">Sélectionner une wilaya</option>
                    {shippingZones.map(zone => (
                      <option key={zone.id} value={zone.wilaya}>
                        {zone.wilaya}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="city">Ville</label>
                  <input
                    type="text"
                    id="city"
                    value={city}
                    onChange={handleCityChange}
                    required
                  />
                </div>
              </div>

              {wilaya && selectedShippingZone && (
                <div className="delivery-options">
                  <h4>Options de livraison</h4>
                  
                  <div className="delivery-choice">
                    <label className="delivery-option">
                      <input
                        type="radio"
                        name="deliveryType"
                        value="home"
                        checked={deliveryType === 'home'}
                        onChange={(e) => setDeliveryType(e.target.value)}
                        required
                      />
                      <div className="delivery-option-content">
                        <div className="delivery-option-info">
                          <i className="fas fa-home"></i>
                          <div>
                            <strong>Livraison à domicile</strong>
                            <p>Livraison directement chez vous</p>
                          </div>
                        </div>
                        <span className="delivery-price">
                          {product.freeShipping || shippingInfo.isFree 
                            ? "Gratuit" 
                            : `${selectedShippingZone.homeDeliveryPrice} DZD`}
                        </span>
                      </div>
                    </label>

                    <label className="delivery-option">
                      <input
                        type="radio"
                        name="deliveryType"
                        value="office"
                        checked={deliveryType === 'office'}
                        onChange={(e) => setDeliveryType(e.target.value)}
                        required
                      />
                      <div className="delivery-option-content">
                        <div className="delivery-option-info">
                          <i className="fas fa-building"></i>
                          <div>
                            <strong>Récupération au bureau</strong>
                            <p>Récupérez votre commande à notre bureau</p>
                          </div>
                        </div>
                        <span className="delivery-price">
                          {product.freeShipping || shippingInfo.isFree 
                            ? "Gratuit" 
                            : `${selectedShippingZone.officePickupPrice} DZD`}
                        </span>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="quantity">Quantité</label>
                  <div className="quantity-selector">
                    <button 
                      type="button"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      <i className="fas fa-minus"></i>
                    </button>
                    <input
                      type="number"
                      id="quantity"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      min="1"
                      max={product.stock}
                    />
                    <button 
                      type="button"
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      disabled={quantity >= product.stock}
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="order-summary">
                <div className="summary-header">
                  <h3>Résumé de votre commande</h3>
                </div>
                
                <div className="summary-content">
                  <div className="summary-row">
                    <div className="label">
                      <i className="fas fa-tag"></i>
                      Prix unitaire
                    </div>
                    <div className="value">
                      <div className="price-display">
                        {getOriginalPrice() && (
                          <span className="original-price">{getOriginalPrice()} DZD</span>
                        )}
                        <span className={product.hasPromotion ? 'promo-price' : ''}>
                          {getCurrentPrice()} DZD
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {product.hasVariants && selectedVariant && (
                    <div className="summary-row variant-summary">
                      <div className="label">
                        <i className="fas fa-tag"></i>
                        Option sélectionnée
                      </div>
                      <div className="value">{selectedVariant.name}</div>
                    </div>
                  )}
                  
                  <div className="summary-row">
                    <div className="label">
                      <i className="fas fa-box"></i>
                      Quantité
                    </div>
                    <div className="value">{quantity}</div>
                  </div>
                  
                  <div className="summary-row">
                    <div className="label">
                      <i className="fas fa-calculator"></i>
                      Sous-total
                    </div>
                    <div className="value">{getCurrentPrice() * quantity} DZD</div>
                  </div>
                  
                  {deliveryType && selectedShippingZone && (
                    <div className="summary-row shipping-cost">
                      <div className="label">
                        <i className="fas fa-truck"></i>
                        Frais de livraison
                        {(product.freeShipping || shippingInfo.isFree) && (
                          <span className="shipping-badge">Gratuit</span>
                        )}
                      </div>
                      <div className="value">
                        {product.freeShipping || shippingInfo.isFree 
                          ? "0"
                          : deliveryType === 'home'
                            ? selectedShippingZone.homeDeliveryPrice
                            : selectedShippingZone.officePickupPrice
                        } DZD
                      </div>
                    </div>
                  )}
                  
                  <div className="summary-row total">
                    <div className="label">
                      <i className="fas fa-receipt"></i>
                      Total à payer
                    </div>
                    <div className="value">{calculateTotal()} DZD</div>
                  </div>
                </div>
              </div>

              <button 
                type="submit" 
                className="submit-order-btn"
                disabled={submitting || product.stock === 0}
              >
                {submitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Traitement en cours...
                  </>
                ) : product.stock <= 0 ? (
                  'Produit indisponible'
                ) : (
                  <>
                    <i className="fas fa-shopping-cart"></i>
                    Commander maintenant
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {recommendedProducts.length > 0 && (
          <div className="recommended-products-section">
            <div className="recommended-products-content">
              <h2>Ces produits pourraient vous intéresser</h2>
              <div className="recommended-products-grid">
                {recommendedProducts.map(product => (
                  <Link 
                    to={`/product/${product.id}`}
                    key={product.id}
                    className="recommended-product-card"
                  >
                    <div className="recommended-product-image">
                      {product.images?.[0] ? (
                        <img 
                          src={product.images[0].url} 
                          alt={product.name}
                          loading="lazy"
                        />
                      ) : (
                        <div className="no-image">
                          <i className="fas fa-image"></i>
                        </div>
                      )}
                      {product.freeShipping && (
                        <span className="free-shipping-badge">
                          <i className="fas fa-truck"></i> Livraison gratuite
                        </span>
                      )}
                    </div>
                    <div className="recommended-product-info">
                      <h3>{product.name}</h3>
                      <div className="product-meta">
                        <span className="price">{product.price} DZD</span>
                        <span className="stock">
                          {product.stock} en stock
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={true}
        closeOnClick={true}
        pauseOnHover={true}
        draggable={true}
        theme="colored"
        limit={1}
      />
    </ShopLayout>
  );
} 